import { AsyncTypeahead } from "react-bootstrap-typeahead";
import { useState } from "react";
import { BoundarySummary } from "../../types/Boundary/BoundarySummary";
import { BoundarySelection } from "../../types/Boundary/BoundarySelection";
import { BoundarySource } from "../../constant/BoundarySource";
import { Boundary } from "../../types/Boundary/Boundary";
import { Option as TypeAheadOption } from "react-bootstrap-typeahead/types/types";
import { SearchOption } from "../../types/Search/SearchOption";
import { GeometryType } from "../../constant/GeometryType";
import { SearchResponse } from "../../types/Search/SearchResponse";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Button, Modal, Stack } from "react-bootstrap";
import {
  faClose,
  faDotCircle,
  faLocation,
} from "@fortawesome/free-solid-svg-icons";
import { Polygon, MultiPolygon } from "ol/geom";
import { useBoundaryService, useSearchService } from "../../hooks";
import ThemedModal from "../../@ui/Modal/ThemedModal";
import { StyledModalTitle } from "../../@ui/StyledModalTitle";
import { useBoundarySelectContext } from "../boundarySelection/BoundarySelectContext";
import { useAuthContext } from "../auth/AuthContext";
import Map from "ol/Map";
import { useViewContext } from "../../context/View/ViewContext";
interface SearchBarProps {
  onChange: (selected: BoundarySelection) => void;
}

export const SearchBar = ({ onChange }: SearchBarProps) => {
  const boundaryService = useBoundaryService();
  const searchService = useSearchService();

  const { removeBoundary } = useBoundarySelectContext();
  const viewContext = useViewContext();
  const view = viewContext.view;
  const [searchOptions, setSearchOptions] = useState<SearchOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingBoundary, setDeletingBoundary] = useState<
    SearchOption | undefined
  >(undefined);
  const authContext = useAuthContext();
  const isLoggedOut = !authContext.user;

  const onSelect = (selected: TypeAheadOption[]) => {
    const selectedOption = selected[0] as SearchOption;

    if (selectedOption.geometryType === GeometryType.LOCATION) {
      onChange({
        boundaryId: selectedOption.id,
        geometry: new Polygon(selectedOption.boundingBox.coordinates),
        name: selectedOption.label,
        source: BoundarySource.TomTomPlace,
        isCustom: false,
      });
    } else {
      boundaryService
        .getById(selectedOption.id)
        .then((result: Boundary) => {
          onChange({
            ...result,
            geometry: new MultiPolygon(result.geometry.coordinates),
            source: BoundarySource.KnownBoundary,
          });
        })
        .catch(() => {});
    }
  };

  const boundarySearch = (input: string) => {
    if (!input) {
      setSearchOptions([]);
      return;
    }

    // avoid invalid request to the api
    if (input.trim() === "") {
      setSearchOptions([]);
      return;
    }

    setIsLoading(true);

    const [minX, minY, maxX, maxY] = view.calculateExtent();

    searchService
      .search(input, undefined, minX, minY, maxX, maxY)
      .then((res: SearchResponse) => {
        const boundaryOptions = res.boundaries.map((b: BoundarySummary) => {
          return {
            id: b.boundaryId,
            label: b.name,
            geometryType: GeometryType.BOUNDARY,
            isCustom: b.isCustom,
            secondaryLabels: [b.state, b.country].filter(Boolean),
          } as SearchOption;
        });

        const locationOptions = res.locations.map((l: any) => {
          return {
            id: l.id,
            label: l.name,
            geometryType: GeometryType.LOCATION,
            geometry: l.point,
            boundingBox: l.boundingBox,
          } as SearchOption;
        });

        setIsLoading(false);
        setSearchOptions(boundaryOptions);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const deleteCustomBoundary = (searchOption: SearchOption) => {
    setSearchOptions([]);
    boundaryService.DeleteCustomBoundary(searchOption.id).then(() => {
      removeBoundary(searchOption.id);
      setDeletingBoundary(undefined);
    });
  };

  const customRenderMenuItem = (option: any, props: any, index: number) => {
    return (
      <Stack gap={2} direction="horizontal" key={option.id}>
        <FontAwesomeIcon
          style={{
            color:
              option.geometryType === GeometryType.BOUNDARY ? "red" : "green",
          }}
          icon={
            option.geometryType === GeometryType.BOUNDARY
              ? faDotCircle
              : faLocation
          }
        />
        <span>
          {option.label}
          {option.secondaryLabels?.length > 0 && `, `}
        </span>{" "}
        {<span>{option.secondaryLabels?.join(", ")}</span>}
        {option.isCustom && (
          <FontAwesomeIcon
            style={{ color: "red" }}
            icon={faClose}
            onClick={() => {
              setDeletingBoundary(option);
            }}
          />
        )}
      </Stack>
    );
  };

  return (
    <>

        <AsyncTypeahead
          id="boundary-typeahead-loggedin"
          isLoading={isLoading}
          onSearch={boundarySearch}
          delay={300}
          renderMenuItemChildren={customRenderMenuItem}
          clearButton
          onChange={onSelect}
          options={searchOptions}
          filterBy={() => true}
          useCache={false}
          placeholder="Search for area"
          className="boundary-typeahead"
          selected={[]}
        />
      

      <ThemedModal
        centered
        show={deletingBoundary !== undefined}
        onHide={() => setDeletingBoundary(undefined)}
        backdrop="static"
      >
        <Modal.Header className="bg-secondary text-white" closeButton>
          <StyledModalTitle>Delete Custom Boundary</StyledModalTitle>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            Are you sure you want to delete the custom boundary{" "}
            <strong>{deletingBoundary?.label}</strong>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="success"
            onClick={() => deleteCustomBoundary(deletingBoundary!)}
          >
            Delete
          </Button>
          <Button
            variant="danger"
            onClick={() => setDeletingBoundary(undefined)}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </ThemedModal>
    </>
  );
};
